/**
 * Module-level cache for segment builder schema data.
 *
 * Modeled after dashboardCache.ts — a simple Map with TTL-based expiry.
 * Caches: datasets, tables, columns, column value distributions.
 * Does NOT cache: segment estimates (change on every rule edit).
 *
 * Cross-tenant isolation: every entry is namespaced by the active account via
 * setAccountScope(). Changing the active account (switch / login / logout) both
 * clears the cache AND re-namespaces all subsequent keys, so a new account can
 * never read the previous account's entries — independent of invalidate timing.
 * (Entries now live 8 h, so timing-independent isolation matters.)
 */

type CacheEntry<T> = {
  data: T;
  fetchedAt: number;
};

const cache = new Map<string, CacheEntry<unknown>>();
const MAX_ENTRIES = 100;

/** Track last-known transform timestamp per data source for freshness checks. */
const lastKnownTransformAt: Record<string, number> = {};

/**
 * Active-account namespace for all cache keys (set via setAccountScope()).
 * Keys are stored as `${_accountScope}::${key}` so one account's entries are
 * physically unreachable from another.
 */
let _accountScope = "";

function scoped(key: string): string {
  return `${_accountScope}::${key}`;
}

/**
 * Point the cache at an account (or "" on logout). Changing the active account
 * clears the cache and re-namespaces subsequent keys — the structural guard
 * against cross-tenant leakage. Safe to call repeatedly; a no-op when unchanged.
 */
export function setAccountScope(accountId: string | null | undefined): void {
  const next = accountId || "";
  if (next === _accountScope) return;
  cache.clear();
  for (const k of Object.keys(lastKnownTransformAt)) delete lastKnownTransformAt[k];
  _accountScope = next;
}

function isExpired(entry: CacheEntry<unknown>, ttlMs: number): boolean {
  return Date.now() - entry.fetchedAt > ttlMs;
}

function evictIfNeeded(): void {
  if (cache.size <= MAX_ENTRIES) return;
  // Evict oldest entry (Map preserves insertion order)
  const oldest = cache.keys().next().value;
  if (oldest) cache.delete(oldest);
}

export function getCached<T>(key: string, ttlMs: number): T | null {
  const k = scoped(key);
  const entry = cache.get(k);
  if (!entry || isExpired(entry, ttlMs)) {
    if (entry) cache.delete(k);
    return null;
  }
  return entry.data as T;
}

export function setCache<T>(key: string, data: T): void {
  evictIfNeeded();
  cache.set(scoped(key), { data, fetchedAt: Date.now() });
}

/** Invalidate all entries (within the active account scope) whose key starts with the prefix. */
export function invalidatePrefix(prefix: string): void {
  const p = scoped(prefix);
  for (const key of [...cache.keys()]) {
    if (key.startsWith(p)) cache.delete(key);
  }
}

/** Clear the entire cache. Call on account switch to prevent cross-tenant leakage. */
export function invalidateAll(): void {
  cache.clear();
  // Also reset transform tracking
  for (const k of Object.keys(lastKnownTransformAt)) {
    delete lastKnownTransformAt[k];
  }
}

/**
 * Check if a data source's underlying data has been refreshed since the cache was populated.
 * If so, invalidate all cached entries for that data source.
 *
 * Call this after loading managed source config to detect pipeline completions.
 */
export function checkFreshness(dataSourceId: string, transformAt: string | null): void {
  if (!transformAt) return;
  const ts = new Date(transformAt).getTime();
  const known = lastKnownTransformAt[dataSourceId];
  if (known && ts > known) {
    // Data has changed since last cache — invalidate this source's entries
    invalidatePrefix(`ds:${dataSourceId}`);
    invalidatePrefix(`tbl:${dataSourceId}`);
    invalidatePrefix(`col:${dataSourceId}`);
    invalidatePrefix(`val:${dataSourceId}`);
    invalidatePrefix(`valneg:${dataSourceId}`);
  }
  lastKnownTransformAt[dataSourceId] = ts;
}
